/**
 * Gallery scene: Person view as a workspace Browse noun (?test=maater-ui#person-view)
 *
 * Filter chips share the h-11 line with the peek header.
 * Not a rail mode or App-group app.
 */
import { useState } from 'react'
import { FileText, User } from 'lucide-react'
import { ExtensionFilterBar, IndexSurfaceShell } from '@/components/integrations/_shared'
import { PreviewPanelToggle } from '@/components/integrations/_shared/preview-panel-toggle'
import { PreviewPaneHeader } from '@/components/features/preview-pane-header'
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar'
import { ShowOverflowLink } from '@/components/ui/show-overflow-link'
import { LIST_OVERFLOW_PRIMARY_CAP, partitionListOverflow } from '@/lib/list-overflow'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

const DEMO_WORKSPACES = [
  'Hop Studios',
  'FlowNotes Production',
  'Personal',
  'Kootenay Lightweb',
  'Harbor Studio',
  'Test workspace',
]

const DEMO_DOCS = [
  { id: '1', title: 'Late-todo preview', meta: '9h ago' },
  { id: '2', title: 'Address critical SEO findings', meta: '4d ago' },
  { id: '3', title: 'Bridging Differences Resource Hub', meta: '4d ago' },
]

export function PersonViewScene() {
  const [wsExpanded, setWsExpanded] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(true)
  const [selectedId, setSelectedId] = useState('1')
  const [segment, setSegment] = useState('recent')
  const ws = partitionListOverflow(DEMO_WORKSPACES, { expanded: wsExpanded })
  const selected = DEMO_DOCS.find((d) => d.id === selectedId)

  return (
    <div className="space-y-4" data-testid={testIds.maaterUiGallery.personView}>
      <p className="text-sm text-muted-foreground">
        <strong className="font-medium text-foreground">Person view</strong> is a
        workspace-owned Browse noun (Slack-style org membership), not an App-group
        People app and not a rail icon. Opening a name from Home / Search / Info
        stays in that row&apos;s org tab group. Roster is{' '}
        <code className="text-xs">/{'{slug}'}/people</code>.{' '}
        <code className="text-xs">/people/:id</code> is a share alias. Identity
        avatars stay on <code className="text-xs">#presence</code>.
      </p>

      <div className="h-[320px] overflow-hidden rounded-md border border-border bg-background">
        <IndexSurfaceShell
          header={
            <div className="flex h-10 items-center justify-between border-b border-border px-3">
              <p className="text-sm font-medium">People / Rowan Sentesy</p>
              <PreviewPanelToggle
                open={previewOpen}
                onToggle={() => setPreviewOpen((v) => !v)}
                testId={testIds.maaterUiGallery.personViewPreviewToggle}
              />
            </div>
          }
          footer={<ListSurfaceStatusBar includePreviewTip />}
        >
          <div className="flex min-h-0 flex-1">
            <div className="flex min-w-0 flex-1 flex-col">
              <ExtensionFilterBar
                singleRow
                search=""
                onSearchChange={() => undefined}
                showSearch={false}
                chipStyle="segment"
                chips={[
                  { id: 'recent', label: 'Recent' },
                  { id: 'activity', label: 'Activity' },
                  { id: 'assignments', label: 'Assignments' },
                ]}
                activeChipId={segment}
                onChipChange={setSegment}
              />
              <div className="flex items-center gap-3 border-b border-border px-3 py-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  RS
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">rowan@example.com</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {ws.primary.map((name) => (
                      <span
                        key={name}
                        className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                  <ShowOverflowLink
                    expanded={wsExpanded}
                    overflowCount={ws.overflowCount}
                    onToggle={() => setWsExpanded((v) => !v)}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex-1 space-y-0.5 p-2">
                {DEMO_DOCS.map((doc) => (
                  <button
                    key={doc.id}
                    type="button"
                    onClick={() => setSelectedId(doc.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
                      selectedId === doc.id ? 'bg-primary/10 text-foreground' : 'hover:bg-muted/60',
                    )}
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">{doc.title}</span>
                    <span className="text-xs text-muted-foreground">{doc.meta}</span>
                  </button>
                ))}
              </div>
            </div>
            {previewOpen && selected ? (
              <div className="w-64 shrink-0 border-l border-border">
                <PreviewPaneHeader
                  row
                  chips={<span className="truncate text-xs">{selected.title}</span>}
                  onClose={() => setPreviewOpen(false)}
                />
                <p className="p-3 text-sm text-muted-foreground">
                  Peek follows the selected row. Double-click opens the document tab.
                </p>
              </div>
            ) : null}
          </div>
        </IndexSurfaceShell>
      </div>
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <User className="h-3.5 w-3.5" />
        Tab title is the person name. Open icons stay on the row menu.
        Cap {LIST_OVERFLOW_PRIMARY_CAP} workspaces before Show N more.
      </p>
    </div>
  )
}
